import { priceAt } from '../../data/seed/seedReference';
import type { ApprovalItem, RegistryState } from '../../data/types';
import { newId } from '../../lib/random';
import type { RegistryAction } from '../actions';
import {
  actorName,
  addCommunication,
  addDocuments,
  appendLedger,
  fundCode,
  today,
  updateHolder,
  withAudit,
} from '../helpers';
import { issueUnitsForApplication } from './applicationHandlers';
import { applyLedgerReversal } from './ledgerHandlers';
import { applyIssueRun } from './raiseHandlers';
import { applyReportRun } from './reportHandlers';
import { applyOfferPublish } from './setupHandlers';

type DecideAction = Extract<RegistryAction, { type: 'APPROVAL_DECIDE' }>;

function applyDistribution(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  return {
    ...state,
    distributions: state.distributions.map((distribution) => {
      if (distribution.id !== item.refId) return distribution;
      if (!approve) return { ...distribution, status: 'DRAFT' };
      return {
        ...distribution,
        status: 'APPROVED',
        approvedBy: approver,
        abaFileName: `${fundCode(state, distribution.fundId)}_${distribution.periodEnd}.aba`,
      };
    }),
  };
}

function applyCall(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  const call = state.calls.find((candidate) => candidate.id === item.refId);
  if (!call) return state;
  const calls = state.calls.map((candidate) =>
    candidate.id === call.id
      ? {
          ...candidate,
          status: approve ? ('ISSUED' as const) : ('REJECTED' as const),
          approvedBy: approve ? approver : undefined,
          issueDate: today,
        }
      : candidate,
  );
  if (!approve) return { ...state, calls };
  const fund = state.funds.find((candidate) => candidate.id === call.fundId);
  const tasks = call.lines.map((line) => ({
    id: newId('t'),
    holderId: line.holderId,
    kind: 'PAY_CALL' as const,
    title: `Pay ${fund?.code ?? ''} capital call ${call.number}`,
    detail: `Amount due ${line.amount.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}. BPAY biller ${fund?.bpayBillerCode ?? ''}, reference ${line.crn}.`,
    dueDate: call.dueDate,
    status: 'OPEN' as const,
    refId: call.id,
  }));
  const next = addCommunication(
    { ...state, calls, tasks: [...tasks, ...state.tasks] },
    {
      template: 'CALL_NOTICE',
      subject: `Capital call notice: ${fund?.code ?? ''} Call ${call.number}`,
      audience: `${fund?.code ?? ''} holders`,
      fundId: call.fundId,
      holderIds: call.lines.map((line) => line.holderId),
      channels: ['EMAIL', 'PUSH'],
    },
  );
  return addDocuments(
    next,
    call.lines.map((line) => ({
      holderId: line.holderId,
      fundId: call.fundId,
      kind: 'CALL_NOTICE' as const,
      refId: call.id,
      name: `${fund?.code ?? ''} capital call notice ${call.number}`,
      date: today,
      sizeKb: 112,
      visibleToInvestor: true,
    })),
  );
}

function applyChangeRequest(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  const request = state.changeRequests.find((candidate) => candidate.id === item.refId);
  if (!request) return state;
  let next: RegistryState = {
    ...state,
    changeRequests: state.changeRequests.map((candidate) =>
      candidate.id === request.id
        ? { ...candidate, status: approve ? 'COMPLETED' : 'REJECTED' }
        : candidate,
    ),
  };
  if (!approve) return next;
  if (request.kind === 'BANK_CHANGE') {
    const bsb = request.details.find((detail) => detail.label === 'New BSB')?.value ?? '063-000';
    next = updateHolder(next, request.holderId, (holder) => ({
      bank: { ...holder.bank, bsb, accountNumber: '000000771', verified: true },
    }));
  }
  const unitClass = next.classes.find((candidate) => candidate.id === request.classId);
  if (
    (request.kind === 'TRANSFER' || request.kind === 'REDEMPTION') &&
    unitClass &&
    request.units
  ) {
    const price = priceAt(unitClass, today);
    const value = Math.round(request.units * price * 100) / 100;
    const common = {
      fundId: unitClass.fundId,
      classId: unitClass.id,
      effectiveDate: today,
      unitPrice: price,
      reference: request.reference,
      postedBy: actorName(state),
      approvedBy: approver,
    };
    if (request.kind === 'TRANSFER' && request.toHolderId) {
      next = appendLedger(next, [
        {
          ...common,
          holderId: request.holderId,
          type: 'TRANSFER_OUT',
          units: -request.units,
          amount: value,
          memo: 'Off-market transfer out',
        },
        {
          ...common,
          holderId: request.toHolderId,
          type: 'TRANSFER_IN',
          units: request.units,
          amount: value,
          memo: 'Off-market transfer in',
        },
      ]);
    } else {
      next = appendLedger(next, [
        {
          ...common,
          holderId: request.holderId,
          type: 'REDEMPTION',
          units: -request.units,
          amount: value,
          memo: 'Redemption at window NAV',
        },
      ]);
    }
  }
  return next;
}

function applyMigration(state: RegistryState, item: ApprovalItem, approve: boolean): RegistryState {
  return {
    ...state,
    migrations: state.migrations.map((job) =>
      job.id === item.refId ? { ...job, status: approve ? 'COMMITTED' : 'RECONCILED' } : job,
    ),
  };
}

function applyUnitIssue(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  if (approve) return issueUnitsForApplication(state, item.refId, approver);
  return {
    ...state,
    applications: state.applications.map((application) =>
      application.id === item.refId ? { ...application, status: 'AWAITING_FUNDS' } : application,
    ),
  };
}

function applyLifecycle(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  if (item.kind === 'CAPITAL_RETURN') {
    return {
      ...state,
      capitalReturns: state.capitalReturns.map((capitalReturn) =>
        capitalReturn.id === item.refId
          ? {
              ...capitalReturn,
              status: approve ? 'APPROVED' : 'DRAFT',
              approvedBy: approve ? approver : undefined,
            }
          : capitalReturn,
      ),
    };
  }
  if (item.kind === 'REDEMPTION_RUN') {
    return {
      ...state,
      redemptionRuns: state.redemptionRuns.map((run) =>
        run.id === item.refId
          ? {
              ...run,
              status: approve ? 'APPROVED' : 'PRICED',
              approvedBy: approve ? approver : undefined,
            }
          : run,
      ),
    };
  }
  const [callId, holderId] = item.refId.split('|');
  return {
    ...state,
    calls: state.calls.map((call) =>
      call.id === callId
        ? {
            ...call,
            lines: call.lines.map((line) =>
              line.holderId === holderId
                ? { ...line, defaultStage: approve ? 'REMEDIED' : 'NOTICE_ISSUED' }
                : line,
            ),
          }
        : call,
    ),
  };
}

export function decideApproval(state: RegistryState, action: DecideAction): RegistryState {
  const item = state.approvals.find((candidate) => candidate.id === action.id);
  const approver = actorName(state);
  if (!item || item.status !== 'PENDING' || item.requestedBy === approver) return state;
  let next: RegistryState = {
    ...state,
    approvals: state.approvals.map((candidate) =>
      candidate.id === item.id
        ? {
            ...candidate,
            status: action.approve ? 'APPROVED' : 'REJECTED',
            decidedBy: approver,
            decidedAt: today,
            comment: action.comment,
          }
        : candidate,
    ),
  };
  switch (item.kind) {
    case 'DISTRIBUTION':
      next = applyDistribution(next, item, action.approve, approver);
      break;
    case 'CAPITAL_CALL':
      next = applyCall(next, item, action.approve, approver);
      break;
    case 'UNIT_ISSUE':
      next = applyUnitIssue(next, item, action.approve, approver);
      break;
    case 'BANK_CHANGE':
    case 'TRANSFER':
    case 'REDEMPTION':
      next = applyChangeRequest(next, item, action.approve, approver);
      break;
    case 'MIGRATION':
      next = applyMigration(next, item, action.approve);
      break;
    case 'ADJUSTMENT':
      next = applyLedgerReversal(next, item, action.approve, approver);
      break;
    case 'ISSUE_RUN':
      next = applyIssueRun(next, item, action.approve, approver);
      break;
    case 'INVESTOR_REPORT':
      next = applyReportRun(next, item, action.approve, approver);
      break;
    case 'OFFER_PUBLISH':
      next = applyOfferPublish(next, item, action.approve);
      break;
    case 'CAPITAL_RETURN':
    case 'REDEMPTION_RUN':
    case 'CALL_DEFAULT':
      next = applyLifecycle(next, item, action.approve, approver);
      break;
    default:
      break;
  }
  return withAudit(
    next,
    action.approve ? 'Approved' : 'Rejected',
    item.title,
    action.comment ?? item.summary,
  );
}
